import React, { useEffect, useState } from "react";
import AdminLayout from "@/components/layout/AdminLayout";
import api from "@/lib/api";
import { toast } from "sonner";
import { Bug, Lightbulb, MessageCircle, MessageSquare } from "lucide-react";

const TYPE_META = {
  bug: { label: "Bug", icon: Bug, cls: "bg-[#E63946]/10 text-[#E63946]" },
  feature: { label: "Feature", icon: Lightbulb, cls: "bg-amber-100 text-amber-700" },
  general: { label: "General", icon: MessageCircle, cls: "bg-slate-100 text-slate-600" },
};
const STATUSES = ["new", "reviewing", "resolved"];

export default function AdminFeedback() {
  const [data, setData] = useState(null);
  const [fType, setFType] = useState("");
  const [fStatus, setFStatus] = useState("");

  const load = () => {
    const params = {};
    if (fType) params.type = fType;
    if (fStatus) params.status = fStatus;
    api.get("/feedback", { params }).then(({ data }) => setData(data)).catch(() => toast.error("Failed to load feedback"));
  };
  useEffect(load, [fType, fStatus]);

  const setStatus = (fid, status) => {
    api.patch(`/feedback/${fid}`, { status }).then(() => { toast.success("Updated"); load(); }).catch(() => toast.error("Failed"));
  };

  return (
    <AdminLayout>
      <div className="p-6 max-w-5xl mx-auto" data-testid="admin-feedback">
        <div className="flex items-center gap-2 mb-1">
          <MessageSquare className="w-6 h-6 text-[#E63946]" />
          <h1 className="font-display text-2xl uppercase tracking-tight text-[#0A192F]">Beta Feedback</h1>
        </div>
        <p className="text-sm text-slate-500 mb-4">Bug reports, feature requests and notes submitted by users, with page/role/browser context.</p>

        {data && (
          <div className="flex flex-wrap gap-3 mb-4">
            {STATUSES.map((s) => (
              <div key={s} className="bg-white border border-slate-200 rounded-sm px-4 py-2">
                <div className="font-data text-xl text-[#0A192F]">{data.counts?.[s] ?? 0}</div>
                <div className="text-[10px] uppercase tracking-wider text-slate-400">{s}</div>
              </div>
            ))}
          </div>
        )}

        <div className="flex gap-2 mb-4">
          <select value={fType} onChange={(e) => setFType(e.target.value)} data-testid="feedback-filter-type" className="border border-slate-200 rounded-sm px-2 py-1.5 text-sm">
            <option value="">All Types</option>
            <option value="bug">Bugs</option>
            <option value="feature">Features</option>
            <option value="general">General</option>
          </select>
          <select value={fStatus} onChange={(e) => setFStatus(e.target.value)} data-testid="feedback-filter-status" className="border border-slate-200 rounded-sm px-2 py-1.5 text-sm">
            <option value="">All Statuses</option>
            {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>

        {!data ? <div className="py-16 text-center text-sm text-slate-400">Loading…</div>
          : data.items.length === 0 ? (
            <div className="py-16 text-center" data-testid="feedback-empty">
              <MessageSquare className="w-10 h-10 text-slate-300 mx-auto" />
              <p className="text-sm text-slate-500 mt-3">No feedback yet. Once users start using the beta, their reports will appear here.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {data.items.map((f) => {
                const m = TYPE_META[f.type] || TYPE_META.general;
                const ctx = f.context || {};
                return (
                  <div key={f.id} data-testid={`feedback-item-${f.id}`} className="bg-white border border-slate-200 rounded-lg p-4">
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                      <span className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-sm ${m.cls}`}><m.icon className="w-3 h-3" /> {m.label}</span>
                      <div className="flex items-center gap-1">
                        {STATUSES.map((s) => (
                          <button key={s} onClick={() => setStatus(f.id, s)}
                            className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded-sm ${f.status === s ? "bg-[#0A192F] text-white" : "bg-slate-100 text-slate-500 hover:bg-slate-200"}`}>{s}</button>
                        ))}
                      </div>
                    </div>
                    <p className="text-sm text-[#0A192F] mt-2 whitespace-pre-wrap">{f.message}</p>
                    <div className="text-[11px] text-slate-400 mt-2 flex flex-wrap gap-x-3 gap-y-0.5">
                      <span>{f.user_email || "anonymous"} ({f.user_role})</span>
                      {ctx.page && <span>· {ctx.page}</span>}
                      {ctx.screen && <span>· {ctx.screen}</span>}
                      {f.created_at && <span>· {new Date(f.created_at).toLocaleString("en-CA")}</span>}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
      </div>
    </AdminLayout>
  );
}
